import { GroupMembership, DropdownOption } from '@aslanpay/portal-common-lib';
import { useManagingCompanyStore } from 'app/store';
import useGroups from 'app/hooks/useGroups';
import useGroupsWithPot from 'app/hooks/useGroupsWithPot/useGroupsWithPot';
import useRecognition from 'app/hooks/useRecognition';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getGroupDisplayNameWithParents } from 'app/utils/groupsWithPot/groupsWithPot';

import { StrictGroupWithPot } from 'app/hooks/useGroupsWithPot/types';

import getEscalations from './Approvals.function';
import { ApprovalsTab, RecognitionWithPot } from './Approvals.types';

function useApprovals(activeTab: ApprovalsTab) {
  const company = useManagingCompanyStore((state) => state.company);
  const companyId = company?.id || '';

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedPotIds = useMemo(() => {
    const raw = searchParams.get('selectedPots');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);

  const setSelectedPotIds = useCallback(
    (ids: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (ids.length > 0) {
          next.set('selectedPots', ids.join(','));
        } else {
          next.delete('selectedPots');
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const {
    data: requests,
    isLoading: isLoadingRequests,
    isFetching: isFetchingRequests,
  } = useRecognition.useListPendingApprovals(
    {
      companyId,
      page: 0,
      size: 9999,
      sortBy: 'submittedAt',
      sort: 'DESC',
    },
    !!companyId && (activeTab === 'pendingApprovals' || activeTab === 'escalations')
  );

  const { flatGroupsWithPots, isLoading: isLoadingGroups } = useGroupsWithPot(
    companyId,
    !!companyId
  );

  const potOptions: DropdownOption[] = useMemo(
    () =>
      flatGroupsWithPots.flatMap((g) =>
        g.pot ? [{ value: g.pot.id, text: getGroupDisplayNameWithParents(g) }] : []
      ),
    [flatGroupsWithPots]
  );

  const { data: rawMemberships, isLoading: isLoadingAllMemberships } =
    useGroups.useFetchPotManagers(companyId, { size: 999 }, !!companyId);

  const allGroupMemberships = useMemo(() => {
    const byGroupId = new Map<string, GroupMembership[]>();
    (rawMemberships?.list ?? []).forEach((m) => {
      const existing = byGroupId.get(m.groupId) ?? [];
      existing.push(m);
      byGroupId.set(m.groupId, existing);
    });
    return flatGroupsWithPots.map((group) => ({
      group,
      members: byGroupId.get(group.id) ?? [],
    }));
  }, [rawMemberships, flatGroupsWithPots]);

  const isRecognitionTab = activeTab === 'pendingApprovals' || activeTab === 'escalations';

  const isLoading =
    (isRecognitionTab && (isLoadingRequests || isFetchingRequests)) ||
    isLoadingGroups ||
    isLoadingAllMemberships;

  // Keyed by the pot rather than the group that owns it, because the pot is what a recognition names. A company
  // or campaign pot belongs to no group, so it has no entry here and no group managers either.
  const groupWithPotByPotId = useMemo(() => {
    const map = new Map<string, { group: StrictGroupWithPot; members: GroupMembership[] }>();
    (allGroupMemberships ?? []).forEach((p) => {
      if (p?.group && p?.group.pot) {
        map.set(p.group.pot.id, {
          group: p.group as StrictGroupWithPot,
          members: p.members,
        });
      }
    });
    return map;
  }, [allGroupMemberships]);

  const list = useMemo<RecognitionWithPot[]>(() => {
    return (requests?.list ?? []).map((item) => {
      const entry = item.sourcePotId ? groupWithPotByPotId.get(item.sourcePotId) : undefined;
      return {
        ...item,
        groupWithPot: entry?.group,
        potManagers: entry?.members.filter((m) => m.membershipRole === 'POT_MANAGER') ?? [],
      };
    });
  }, [requests, groupWithPotByPotId]);

  const pendingApprovals = useMemo(() => {
    const all = list ?? [];
    if (selectedPotIds.length === 0) return all;
    return all.filter((r) => !!r.sourcePotId && selectedPotIds.includes(r.sourcePotId));
  }, [list, selectedPotIds]);

  const escalations = useMemo(() => {
    const all = getEscalations(list);
    if (selectedPotIds.length === 0) return all;
    return all.filter((r) => !!r.sourcePotId && selectedPotIds.includes(r.sourcePotId));
  }, [list, selectedPotIds]);

  return {
    isLoading,
    pendingApprovals,
    escalations,
    selectedPotIds,
    setSelectedPotIds,
    potOptions,
  };
}

export default useApprovals;
